// ============================================================================
// frontend/src/components/tenant/OAuthSection.tsx
// ============================================================================

import React from 'react';
import { TenantFormData, OAuthProvider } from '@/types/tenant';

interface OAuthSectionProps {
  formData: TenantFormData;
  setFormData: (data: TenantFormData) => void;
}

export const OAuthSection: React.FC<OAuthSectionProps> = ({
  formData,
  setFormData
}) => {
  const addOAuthProvider = (type: 'google' | 'azure-ad') => {
    const newProvider: OAuthProvider = {
      type,
      enabled: true,
      config: {
        client_id: '',
        client_secret: '',
        ...(type === 'azure-ad' && { tenant_id: '' })
      }
    };
    setFormData({
      ...formData,
      oauth_providers: [...formData.oauth_providers, newProvider]
    });
  };

  const removeOAuthProvider = (index: number) => {
    const updated = formData.oauth_providers.filter((_, i) => i !== index);
    setFormData({ ...formData, oauth_providers: updated });
  };

  const updateOAuthProvider = (index: number, field: string, value: string) => {
    const updated = [...formData.oauth_providers];
    updated[index] = {
      ...updated[index],
      config: {
        ...updated[index].config,
        [field]: value
      }
    };
    setFormData({ ...formData, oauth_providers: updated });
  };

  const hasProvider = (type: 'google' | 'azure-ad') => {
    return formData.oauth_providers.some(p => p.type === type);
  };

  const getCallbackUrl = (type: 'google' | 'azure-ad') => {
    const subdomain = formData.subdomain || '{subdomain}';
    return `https://${subdomain}.smartlawyer.ai/auth/oauth/${type}/callback`;
  };

  return (
    <div className="rounded-lg border bg-card p-6 space-y-6">
      <div className="flex items-center justify-between border-b pb-3">
        <div>
          <h2 className="text-xl font-semibold">🔐 Authentification OAuth</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Ajoutez des méthodes de connexion pour vos utilisateurs
          </p>
        </div>
      </div>

      {formData.oauth_providers.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <p>Aucun provider OAuth configuré</p>
          <p className="text-xs mt-1">Les utilisateurs devront utiliser email/mot de passe</p>
        </div>
      ) : (
        <div className="space-y-4">
          {formData.oauth_providers.map((provider, index) => (
            <div key={index} className="p-4 border rounded-md bg-muted/30 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {provider.type === 'google' ? (
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                      <svg className="w-6 h-6" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#0078D4] flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M0 0h11.377v11.372H0zm12.623 0H24v11.372H12.623zM0 12.623h11.377V24H0zm12.623 0H24V24H12.623z"/>
                      </svg>
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold">
                      {provider.type === 'google' ? 'Google OAuth' : 'Azure Active Directory'}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Authentification via {provider.type === 'google' ? 'Google' : 'Microsoft'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeOAuthProvider(index)}
                  className="text-destructive hover:bg-destructive/10 p-2 rounded-md transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>

              <div className="space-y-3 pl-13">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Client ID {provider.type === 'azure-ad' && '(Application ID)'}
                  </label>
                  <input
                    type="text"
                    required
                    value={provider.config.client_id}
                    onChange={(e) => updateOAuthProvider(index, 'client_id', e.target.value)}
                    placeholder={provider.type === 'google' ? '123456789-abc.apps.googleusercontent.com' : '12345678-1234-1234-1234-123456789abc'}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Client Secret</label>
                  <input
                    type="password"
                    required
                    value={provider.config.client_secret}
                    onChange={(e) => updateOAuthProvider(index, 'client_secret', e.target.value)}
                    placeholder="•••••••••"
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                  />
                </div>

                {provider.type === 'azure-ad' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Tenant ID</label>
                    <input
                      type="text"
                      required
                      value={provider.config.tenant_id || ''}
                      onChange={(e) => updateOAuthProvider(index, 'tenant_id', e.target.value)}
                      placeholder="12345678-1234-1234-1234-123456789abc"
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                    />
                  </div>
                )}

                <div className="p-3 bg-muted rounded-md">
                  <p className="text-xs font-medium mb-1">Callback URL à configurer :</p>
                  <code className="text-xs bg-background px-2 py-1 rounded">
                    {getCallbackUrl(provider.type)}
                  </code>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={() => addOAuthProvider('google')}
          disabled={hasProvider('google')}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent disabled:opacity-50 transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Ajouter Google OAuth
        </button>

        <button
          type="button"
          onClick={() => addOAuthProvider('azure-ad')}
          disabled={hasProvider('azure-ad')}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-input bg-background text-sm font-medium hover:bg-accent disabled:opacity-50 transition-colors"
        >
          <div className="w-5 h-5 rounded bg-[#0078D4] flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M0 0h11.377v11.372H0zm12.623 0H24v11.372H12.623zM0 12.623h11.377V24H0zm12.623 0H24V24H12.623z"/>
            </svg>
          </div>
          Ajouter Azure AD
        </button>
      </div>
    </div>
  );
};